// Parsing pasted d100-style tables into loot entries.
//
// Each row starts with a roll range and then the item, separated by |, a tab, a colon, or spaces:
//   01-15 | Potion of Healing
//   16–35	Spell Scroll (Cantrip)
//   36 Bag of Holding
//   96-00 2 x Potion of Fire Breath
// 00 counts as 100, and the weight is how many numbers the range covers.
// Lines that don't start with a number (like headers) are skipped.

export interface ParsedLootRow {
  line: number; // 1 based, for showing problems
  lo: number;
  hi: number;
  weight: number;
  count: number;
  name: string;
}

export interface ParseResult {
  rows: ParsedLootRow[];
  skipped: { line: number, text: string }[];
}

const ROW = /^\s*(\d{1,3})(?:\s*[-–—]\s*(\d{1,3}))?\s*(?:[|:\t]|\s)\s*(.+?)\s*$/;
const COUNT = /^(\d+)\s*[x×]\s+(.+)$/i;

const rollNumber = (str: string) => {
  const n = +str;
  // On a d100 table "00" is 100
  return n === 0 && str.length > 1 ? 100 : n;
};

export const parseLootRows = (text: string): ParseResult => {
  const rows: ParsedLootRow[] = [];
  const skipped: { line: number, text: string }[] = [];
  const lines = text.split(/\r?\n/);
  lines.forEach((text_line, index) => {
    if (text_line.trim() === "") {
      return;
    }
    const match = ROW.exec(text_line);
    if (!match) {
      skipped.push({ line: index + 1, text: text_line });
      return;
    }
    const lo = rollNumber(match[1]);
    const hi = match[2] !== undefined ? rollNumber(match[2]) : lo;
    let name = match[3].replace(/^[|:\t\s]+/, "").trim();
    let count = 1;
    const count_match = COUNT.exec(name);
    if (count_match) {
      count = +count_match[1];
      name = count_match[2].trim();
    }
    if (hi < lo || name === "") {
      skipped.push({ line: index + 1, text: text_line });
      return;
    }
    rows.push({ line: index + 1, lo, hi, weight: hi - lo + 1, count, name });
  });
  return { rows, skipped };
};

// Lowercase, straight quotes, no punctuation that tables write differently, single spaces.
export const normalizeItemName = (name: string) => name
  .toLowerCase()
  .replace(/[‘’`]/g, "'")
  .replace(/[“”]/g, '"')
  .replace(/[–—]/g, "-")
  .replace(/[',."]/g, "")
  .replace(/\s+/g, " ")
  .trim();

export const makeItemMatcher = <T extends { name: string }>(items: T[]) => {
  const by_name = new Map<string, T[]>();
  for (const item of items) {
    const key = normalizeItemName(item.name);
    const list = by_name.get(key);
    if (list) {
      list.push(item);
    } else {
      by_name.set(key, [item]);
    }
  }
  // Every item with that name. More than one means it's ambiguous (like the same item in two source books).
  return (name: string): T[] => by_name.get(normalizeItemName(name)) ?? [];
};
